import { useEffect, useRef, type ReactNode } from 'react';
import { cx } from './primitives';

/**
 * Accessible modal dialog.
 *
 * Escape closes, focus moves into the dialog on open and returns to the
 * previously focused element on close, and background scrolling is locked.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  /** Set false for a decision the user must make explicitly. */
  dismissible = true,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  width?: 'md' | 'lg' | 'xl';
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Move focus to the first control inside the dialog, or the dialog itself.
    const focusable = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? dialogRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, dismissible, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-[1px]"
      // Only a dismissible dialog closes on a backdrop click.
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={description ? 'modal-description' : undefined}
        tabIndex={-1}
        className={cx(
          'flex w-full flex-col rounded-card border border-line bg-surface shadow-modal outline-none',
          /*
           * The dialog is capped to the viewport and scrolls its own body, so a
           * long form keeps its title and its actions in view instead of pushing
           * them off-screen and leaving the user to hunt for the save button.
           */
          'max-h-[calc(100dvh-2rem)]',
          width === 'xl' ? 'max-w-4xl' : width === 'lg' ? 'max-w-2xl' : 'max-w-lg',
        )}
      >
        <div className="shrink-0 border-b border-line-soft px-6 py-5">
          <h2 id="modal-title" className="text-heading font-semibold text-ink">
            {title}
          </h2>
          {description ? (
            <p id="modal-description" className="mt-1 text-micro text-ink-muted">
              {description}
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer ? (
          <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-line-soft px-6 py-4 sm:flex-row sm:justify-end">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
